import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil, maxBy } from 'lodash'
import pluralize from 'pluralize'
import { IconPlus } from '@tabler/icons-react'

import { apiErrorMessage } from '@/api'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Drawer } from '@/components/drawer/Drawer'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'

import { AiAnswersPlaybookField } from './AiAnswersPlaybookField'

import {
  ALL_PLAYBOOKS,
  ANSWER_SAVE_FAILED,
  VERIFIED_ANSWER_MAX_ANSWER_CHARS,
  VERIFIED_ANSWER_MAX_QUESTION_CHARS,
  findMatchingVerifiedAnswers,
  formatPlaybookScope,
  getCapReason,
  isScopeUnused,
} from './AiAnswers.helpers'
import { createVerifiedAnswer, updateVerifiedAnswer } from './verifiedAnswers.api'

const UNAVAILABLE = "Verified answers couldn't be loaded. Refresh to try again."
const ANSWER_DELETED = 'That verified answer was deleted. Refresh the page to see current answers.'
const NOT_FOUND = 404

// A pre-filled value can start past agent-gpt's limit; maxLength only stops typing.
const getOverLimitReason = (noun, size, limit) => {
  if (size <= limit) {
    return null
  }

  return `This ${noun} is ${size.toLocaleString()} characters. Shorten it to ${limit.toLocaleString()} to save.`
}

/**
 * @import import { AiAnswersAnswerDrawer } from '@/views/AiAnswers/AiAnswersAnswerDrawer'
 * @param {object} props
 * @param {object[]} [props.asks] The selected knowledge gap rows; the answer links all of them.
 *   Empty adds an answer from scratch, before anyone has asked the question.
 * @param {string} [props.initialAnswer] Pre-fills the answer when correcting what AgentGPT said;
 *   titles the drawer "Edit answer"
 * @param {Function} [props.trigger] Render prop `(open) => node` for a custom opener; defaults to a
 *   labelled button
 * @param {object} [props.verifiedAnswers] agent-gpt's verified answers list with the org's usage;
 *   null while loading or after a failed load, which disables the action
 * @param {boolean} [props.loading] Whether the verified answers are fetching
 * @param {object} props.playbooks The org's playbook options and their load state
 * @param {number} props.organizationId The org the answer is written to
 * @param {Function} props.onAnswered Called after the answer saves
 */
export const AiAnswersAnswerDrawer = ({
  asks = [],
  initialAnswer = '',
  trigger,
  verifiedAnswers,
  loading = false,
  playbooks,
  organizationId,
  onAnswered,
}) => {
  const dispatch = useDispatch()
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [configCids, setConfigCids] = useState([])
  // The verified answer being edited instead of adding a new one; null while adding.
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const isEditing = !isNil(editing)
  const fromScratch = isEmpty(asks)
  const correcting = !isEmpty(initialAnswer)
  const label = fromScratch ? 'Add answer' : `Answer ${asks.length.toLocaleString()} selected`
  const triggerProps = fromScratch ? { icon: <IconPlus /> } : { brand: true }

  if (isNil(verifiedAnswers)) {
    return (
      <Button small disabled disabledTooltip={loading ? null : UNAVAILABLE} {...triggerProps}>
        {label}
      </Button>
    )
  }

  // No canonical question exists until asks are grouped, so the newest ask stands in.
  const initialQuestion = fromScratch ? '' : maxBy(asks, (row) => new Date(row.created_at)).question
  const trimmedQuestion = question.trim()
  const trimmedAnswer = answer.trim()
  const matches = findMatchingVerifiedAnswers(trimmedQuestion, verifiedAnswers.items)
  const newestMatch = matches[0]
  // Asks linked to an answer AgentGPT serves to no one would leave the gaps list unanswered.
  const matchUnused = !isNil(newestMatch) && isScopeUnused(newestMatch.config_cids, playbooks)
  const questionToSave = isEditing ? editing.question : trimmedQuestion
  const capReason = getCapReason(
    verifiedAnswers,
    questionToSave.length + trimmedAnswer.length,
    editing
  )
  // An edit keeps the saved answer's question, so only its answer is checked.
  const questionReason = isEditing
    ? null
    : getOverLimitReason('question', trimmedQuestion.length, VERIFIED_ANSWER_MAX_QUESTION_CHARS)
  const answerReason = getOverLimitReason(
    'answer',
    trimmedAnswer.length,
    VERIFIED_ANSWER_MAX_ANSWER_CHARS
  )
  const blank = isEmpty(trimmedAnswer) || (!isEditing && isEmpty(trimmedQuestion))
  // Sending the answer records an edit in its history, so an unchanged one is left out.
  const answerChanged = isEditing && trimmedAnswer !== editing.answer
  // With no asks to link, an edit has nothing to save until the answer changes.
  const nothingToSave = isEditing && fromScratch && !answerChanged

  const getTitle = () => {
    if (isEditing) {
      return 'Edit verified answer'
    }
    if (fromScratch) {
      return 'Add answer'
    }
    if (correcting) {
      return 'Edit answer'
    }
    return `Answer ${pluralize('question', asks.length, true)}`
  }

  const handleOpen = (open) => {
    setQuestion(initialQuestion)
    setAnswer(initialAnswer)
    setConfigCids([])
    setEditing(null)
    setSaveError(null)
    open()
  }

  const handleEditMatch = () => {
    setEditing(newestMatch)
    setAnswer(newestMatch.answer)
    setSaveError(null)
  }

  const handleSave = async (close) => {
    setSaving(true)
    setSaveError(null)

    try {
      await dispatch(
        isEditing
          ? updateVerifiedAnswer({
              organizationId,
              answerId: editing.id,
              answer: answerChanged ? trimmedAnswer : undefined,
              sourceAgentQuestionIds: asks.map((row) => row.id),
            })
          : createVerifiedAnswer({
              organizationId,
              question: trimmedQuestion,
              answer: trimmedAnswer,
              configCids,
              sourceAgentQuestionIds: asks.map((row) => row.id),
            })
      )
    } catch (error) {
      setSaving(false)
      // Another manager deleted the matched answer after this page loaded its list.
      if (isEditing && error?.status === NOT_FOUND) {
        setSaveError(ANSWER_DELETED)
        return
      }
      // Cap and already-answered refusals are 409 bodies; a 403 arrives as an Error.
      setSaveError(error?.detail?.message ?? apiErrorMessage(error, ANSWER_SAVE_FAILED))
      return
    }

    // Reset before handing off: from Sources this component stays mounted for the next answer,
    // while clearing a gap selection unmounts it, so no state updates after onAnswered.
    toast.success(isEditing ? 'Verified answer updated' : 'Verified answer saved')
    setSaving(false)
    close()
    onAnswered()
  }

  return (
    <Drawer
      title={getTitle()}
      syncUrl={false}
      warnOnClose={
        isEditing
          ? answer !== editing.answer
          : answer !== initialAnswer || !isEmpty(configCids) || question !== initialQuestion
      }
      trigger={(open) =>
        isNil(trigger) ? (
          <Button small onClick={() => handleOpen(open)} {...triggerProps}>
            {label}
          </Button>
        ) : (
          trigger(() => handleOpen(open))
        )
      }
    >
      {(close) => (
        <>
          <div className="padding-medium flex-column medium-gap">
            {!fromScratch && (
              <FormField label={correcting ? 'Asked' : 'Selected questions'}>
                <ul className="ai-answers-list">
                  {asks.map((row) => (
                    <li key={row.id} className="ai-answers-list__item">
                      {row.question}
                    </li>
                  ))}
                </ul>
              </FormField>
            )}
            {isEditing ? (
              <FormField label="Question">
                <p>{editing.question}</p>
              </FormField>
            ) : (
              <>
                <FormField label="Question" required>
                  <TextArea
                    rows={3}
                    value={question}
                    maxLength={VERIFIED_ANSWER_MAX_QUESTION_CHARS}
                    onChange={(event) => setQuestion(event.target.value)}
                    data-testid="answer-drawer-question"
                  />
                </FormField>
                {!isEmpty(matches) && (
                  <Banner
                    informative
                    className="no-margin"
                    header="Your team already has a verified answer for this question"
                    dataTestId="answer-drawer-match"
                  >
                    <p className="ai-answers-match__answer">{newestMatch.answer}</p>
                    <p className="flex-align-center small-gap">
                      {`Used for: ${
                        isEmpty(newestMatch.config_cids)
                          ? ALL_PLAYBOOKS
                          : formatPlaybookScope(newestMatch.config_cids, playbooks)
                      }`}
                      {matchUnused && (
                        <Pill warning xs>
                          Not in use
                        </Pill>
                      )}
                    </p>
                    {matchUnused && (
                      <p>
                        AgentGPT isn&apos;t using it, since every playbook it was limited to was
                        deleted. Save a new answer instead.
                      </p>
                    )}
                    {/* Asks carry no playbook, so the manager judges whether they came from these. */}
                    {!isEmpty(newestMatch.config_cids) && !fromScratch && !matchUnused && (
                      <p>
                        If these questions came from another playbook, save a new answer instead.
                      </p>
                    )}
                    {matches.length > 1 && (
                      <p>
                        {`${matches.length.toLocaleString()} verified answers have this question. This is the newest.`}
                      </p>
                    )}
                    {!matchUnused && answer !== initialAnswer && !isEmpty(trimmedAnswer) && (
                      <p>Editing it replaces the answer you&apos;ve written here.</p>
                    )}
                    {!matchUnused && (
                      <div className="mt">
                        <Button
                          small
                          onClick={handleEditMatch}
                          data-testid="answer-drawer-edit-match"
                        >
                          Edit that answer
                        </Button>
                      </div>
                    )}
                  </Banner>
                )}
              </>
            )}
            <FormField label="Answer" required>
              <TextArea
                rows={8}
                value={answer}
                maxLength={VERIFIED_ANSWER_MAX_ANSWER_CHARS}
                onChange={(event) => setAnswer(event.target.value)}
                data-testid="answer-drawer-answer"
              />
            </FormField>
            {/* An edit's scope is changed from the answers table, which confirms widening it. */}
            {!isEditing && (
              <AiAnswersPlaybookField
                playbooks={playbooks}
                value={configCids}
                onChange={setConfigCids}
              />
            )}
            <p className="muted-text">
              {`${verifiedAnswers.count.toLocaleString()} of ${verifiedAnswers.limit.toLocaleString()} verified answers used`}
            </p>
            {capReason && <ErrorMessage warning content={capReason} />}
            {questionReason && <ErrorMessage warning content={questionReason} />}
            {answerReason && <ErrorMessage warning content={answerReason} />}
            {saveError && <ErrorMessage content={saveError} />}
          </div>
          <div className="drawer-footer">
            <div className="flex-align-center flex-end">
              <Button
                brand
                loading={saving}
                disabled={
                  blank ||
                  nothingToSave ||
                  !isNil(capReason) ||
                  !isNil(questionReason) ||
                  !isNil(answerReason) ||
                  saving
                }
                onClick={() => handleSave(close)}
                data-testid="answer-drawer-save"
              >
                Save answer
              </Button>
            </div>
          </div>
        </>
      )}
    </Drawer>
  )
}
